import clsx from 'clsx';
import { CircleUserRound, Download, FileAudio, FileText, FileVideo, Headphones, Pause, Play, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DialogHeader } from '@/components';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui';
import { useERSContext } from '@/context';
import { useAIProvider } from '@/hooks/useAIProvider';
import { constants } from '@/lib/constants';
import { cn, FILE_TYPE_EXTENSIONS, getPresenceColor } from '@/lib/utils';
import PDFViewer from '@/components/shared/PDFViewer/PDFViewer';

export const ChatMessage = ({
  userId,
  id,
  sender,
  text,
  displayName,
  time,
  type,
  fileUrl,
  fileName,
  isHighlighted = false,
  nickname,
  userType,
  isPlaybackMessage,
  duration,
  glLat,
  glLon,
}) => {
  const { users } = useERSContext();
  const { callTranscribeAudio } = useAIProvider({ temperature: 0.6 });
  const { t } = useTranslation();
  const isMe = sender === constants.CHAT_PANEL.ME;
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [showTranscription, setShowTranscription] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [audioDuration, setAudioDuration] = useState(duration || 0);

  const SYSTEM_KEYWORDS = [
    t('CHAT_PANEL.HAS_ACCEPTED_THE_INCIDENT'),
    t('CHAT_PANEL.INCIDENT_HAS_BEEN_UPDATED'),
    t('CHAT_PANEL.INCIDENT_HAS_BEEN_RESOLVED'),
    t('CHAT_PANEL.YOU_HAVE_JOINED_THE_GROUP'),
  ];

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const onLoadedMetadata = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setAudioDuration(audio.duration);
      }
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);

    // Cleanup listeners on unmount
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, [fileUrl]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (isPlaying) {
        audio.pause();
      } else {
        await audio.play();
      }
    } catch (error) {
      console.error('Error playing audio:', error);
    }
  };

  const onSeek = e => {
    const audio = audioRef.current;
    if (!audio) return;

    const seekTime = Number(e.target.value);
    audio.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const formatTime = time => {
    if (isNaN(time)) return '00:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins < 10 ? '0' + mins : mins}:${secs < 10 ? '0' + secs : secs}`;
  };

  // Detect file type from extension
  const getFileTypeFromName = fileName => {
    if (!fileName) return constants.CHAT_PANEL.FILE;
    const ext = fileName.toLowerCase().split('.').pop();

    for (const [type, extensions] of Object.entries(FILE_TYPE_EXTENSIONS)) {
      if (extensions.includes(ext)) return type;
    }

    return constants.CHAT_PANEL.FILE;
  };

  const getDisplayType = () =>
    fileUrl && fileName ? getFileTypeFromName(fileName) : type || constants.CHAT_PANEL.TEXT;
  const displayType = getDisplayType();

  const getFileIcon = fileType => {
    switch (fileType) {
      case constants.CHAT_PANEL.VIDEO:
        return <FileVideo size={18} className="text-primary" />;
      case constants.CHAT_PANEL.AUDIO:
        return <FileAudio size={18} className="text-primary" />;
      default:
        return <FileText size={18} className="text-primary" />;
    }
  };

  // --- Transcription logic ---
  const fetchTranscription = async () => {
    if (!fileUrl || isTranscribing) return;
    setIsTranscribing(true);
    try {
      const response = await fetch(fileUrl);
      const audioBlob = await response.blob();
      const audioFile = new File([audioBlob], fileName || 'audio.webm', { type: audioBlob.type });

      const transcribedText = await transcribeAudio(audioFile);
      setTranscription(transcribedText);
      setShowTranscription(true);
    } catch (error) {
      console.error('Error transcribing audio:', error);
      setTranscription(t('CHAT_PANEL.FAILED_TO_TRANSCRIBE_AUDIO'));
      setShowTranscription(true);
    } finally {
      setIsTranscribing(false);
    }
  };

  const transcribeAudio = async audioFile => {
    try {
      const response = await callTranscribeAudio(audioFile, {
        model: 'whisper-1',
        language: 'en',
        response_format: constants.CHAT_PANEL.TEXT,
      });

      return response.trim();
    } catch (error) {
      console.error('OpenAI transcription error:', error);
      throw error;
    }
  };

  const handleTranscriptionClick = () => {
    if (showTranscription) setShowTranscription(false);
    else if (transcription) setShowTranscription(true);
    else fetchTranscription();
  };

  const handleImageClick = () => {
    if (displayType === 'image' && fileUrl) setShowImagePreview(true);
  };

  const handleDownloadImage = () => {
    if (fileUrl) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleClosePreview = () => setShowImagePreview(false);

  const isSystemMessage =
    sender === constants.CHAT_PANEL.UNKNOWN ||
    (displayName === undefined &&
      text &&
      SYSTEM_KEYWORDS.some(keyword => text.toLowerCase().includes(keyword.toLowerCase())));

  if (isSystemMessage) {
    return (
      <div id={id} className="flex justify-center my-2">
        <p className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full text-center">
          {text || t('CHAT_PANEL.SYSTEM_MESSAGE')}
        </p>
      </div>
    );
  }

  const handlePdfClick = () => {
    setIsModalOpen(true);
  };

  const handleUsersPresenece = (nickname_od_id, type) => {
    let usersObj;
    if (type == 'group') {
      usersObj = users?.filter(user => user?.id?.split('@')[0] == nickname_od_id);
    } else {
      usersObj = users?.filter(user => user?.id == nickname_od_id);
    }
    return getPresenceColor(usersObj[0]?.presence);
  };

  return (
    <>
      <div
        id={id}
        className={cn(
          'flex mb-3',
          isMe ? 'justify-end' : 'justify-start',
          isHighlighted && 'bg-warning-muted-hover rounded-lg p-1'
        )}
      >
        {!isMe && (
          <div className="relative w-8 h-8 flex justify-center items-center mr-2 flex-shrink-0">
            <CircleUserRound size={24} absoluteStrokeWidth className="text-icon" />
            <span
              className={clsx(
                'absolute bottom-0.5 right-0.5 block h-2.5 w-2.5 rounded-full border-2 border-onColor',
                userType == 'user' ? handleUsersPresenece(userId, userType) : handleUsersPresenece(nickname, userType)
              )}
            />
          </div>
        )}

        <div className={cn('flex flex-col max-w-xs', isMe ? 'items-end' : 'items-start')}>
          <div
            className={cn(
              'p-3 rounded-lg',
              isMe ? 'bg-primary text-inverse rounded-br-none' : 'bg-secondary rounded-tl-none'
            )}
          >
            {/* Sender name */}
            {!isMe && (
              <p className="font-semibold text-sm text-left mb-1">
                {userType === 'user' ? nickname : displayName || t('CHAT_PANEL.UNKNOWN')}
              </p>
            )}

            {/* Text message */}
            {displayType === constants.CHAT_PANEL.TEXT && <p className="text-left">{text}</p>}

            {/* Image attachment */}
            {displayType === 'image' && fileUrl && (
              <div className="flex flex-col">
                <img
                  src={fileUrl}
                  alt="sent-img"
                  className="rounded-lg max-h-48 object-cover mt-1 cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={handleImageClick}
                />
                {text && <p className="mt-2 text-sm text-left">{text}</p>}
              </div>
            )}

            {/* Video attachment */}
            {displayType === constants.CHAT_PANEL.VIDEO && fileUrl && (
              <div className="rounded-lg">
                <div className="rounded-lg flex flex-col bg">
                  <video controls src={fileUrl} className="rounded-t-lg max-h-48" />
                  {fileName && <p className="text-sm mt-1 bg text-left p-2 text">{fileName}</p>}
                </div>
                {text && <p className="mt-2 text-sm text-left rounded-b-lg">{text}</p>}
              </div>
            )}

            {/* Audio attachment */}
            {displayType === constants.CHAT_PANEL.AUDIO && fileUrl && (
              <div className="w-80 flex flex-col rounded-lg">
                {/* Hidden native audio element */}
                <audio ref={audioRef} src={fileUrl} preload="auto" />

                {/* Custom controls */}
                <div className="bg rounded-lg">
                  <div className="flex items-center gap-2 p-2 bg-opacity-20 rounded-t">
                    <div>
                      <Button
                        onClick={togglePlay}
                        variant="outline"
                        size="icon"
                        aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
                      >
                        {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                      </Button>
                    </div>

                    <div className="flex-1 flex flex-col gap-1">
                      <input
                        type="range"
                        min="0"
                        max={audioDuration || 0}
                        value={currentTime}
                        onChange={onSeek}
                        className="w-full cursor-pointer"
                        style={{
                          appearance: 'none',
                          height: '6px',
                          borderRadius: '3px',
                          background: `linear-gradient(to right, #3b82f6 ${audioDuration ? (currentTime / audioDuration) * 100 : 0}%, #d1d5db ${audioDuration ? (currentTime / audioDuration) * 100 : 0}%)`,
                          outline: 'none',
                        }}
                      />
                      <span className="text-xs text tabular-nums">
                        {formatTime(currentTime)} / {formatTime(audioDuration)}
                      </span>
                    </div>
                  </div>
                  {!text && fileName && !showTranscription && (
                    <div className="flex items-center text-icon gap-1 mt-1 px-2 bg bg-opacity-20 rounded-b-lg">
                      <Headphones size={16} className="text-icon" />
                      <p className="text-sm text text-left p-2 bg rounded-b-lg w-60 truncate">{fileName}</p>
                    </div>
                  )}

                  {/* --- Transcription section commented out --- */}
                  {/* <div className="mt-2 flex flex-col gap-2">
                    <button
                      onClick={handleTranscriptionClick}
                      disabled={isTranscribing}
                      className={cn(
                        'flex items-center gap-2 px-3 py-1 rounded-t-md text-xs font-medium transition-colors',
                        isMe ? 'bg-info-muted text' : 'bg-strong text'
                      )}
                    >
                      {isTranscribing ? (
                        <>
                          <Loader2 size={12} className="animate-spin" />
                          {t('CHAT_PANEL.TRANSCRIBING')}
                        </>
                      ) : (
                        <>
                          <MessageSquare size={12} />
                          {showTranscription ? t('CHAT_PANEL.HIDE_TRANSCRIPTION') : t('CHAT_PANEL.SHOW_TRANSCRIPTION')}
                        </>
                      )}
                    </button>
 
                    {showTranscription && transcription && (
                      <div className={cn('p-2 rounded text-sm', isMe ? 'bg-info-muted-hover text' : 'bg-strong text')}>
                        {transcription}
                      </div>
                    )}
 
                    {text && !showTranscription && <p className="text-sm text-left text">{text}</p>}
                  </div> */}
                </div>
              </div>
            )}

            {/* File attachment */}
            {displayType === constants.CHAT_PANEL.FILE && fileUrl && (
              <div className="flex flex-col mt-1 cursor-pointer" onClick={handlePdfClick}>
                <div className="flex justify-center items-center rounded-t-md overflow-hidden w-[247px] h-[123px] ">
                  <PDFViewer file={fileUrl} showAll={false} pageNumber={1} scale={0.9} />
                </div>
                <div className="flex items-center space-x-2 px-3 py-2 bg rounded-b-md  text">
                  <span className="text-xl">{getFileIcon(displayType)}</span>
                  <span className="text-xs text underline cursor-pointer">{fileName}</span>
                </div>
                {text && <p className="mt-2 text-sm text-left">{text}</p>}
              </div>
            )}

            <p className={cn('text-xs mt-1 flex justify-end w-full', isMe && 'text-inverse')}>{time}</p>
          </div>
          {/* PDF Modal for full view */}
          <Dialog open={isModalOpen} onOpenChange={() => setIsModalOpen(false)}>
            <DialogContent className="p-4" style={{ maxWidth: '1000px', height: '800px' }}>
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold text">{fileName}</DialogTitle>
              </DialogHeader>
              <PDFViewer file={fileUrl} showAll={true} scale={1.1} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Image Preview Modal */}
      {showImagePreview && (
        <div className="fixed inset-0 bg bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-secondary rounded-lg max-w-4xl max-h-full w-full h-full flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <button onClick={handleClosePreview} className="p-2 cursor-pointer rounded-full transition-colors">
                <X size={24} />
              </button>
              <h3 className="text-lg font-semibold">{t('CHAT_PANEL.ATTACHED_FILES_TITLE')}</h3>
              <button onClick={handleDownloadImage} className="p-2rounded-full transition-colors">
                <Download size={24} />
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
              <img src={fileUrl} alt={fileName || 'Preview'} className="max-w-full max-h-full object-contain rounded" />
            </div>
            {(fileName || text) && (
              <div className="p-4 border-t bg-strong">
                {fileName && <p className="text-sm font-medium text">{fileName}</p>}
                {text && <p className="text-sm text mt-1">{text}</p>}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
